import * as Base from '@/components/base/table'
import { Heading } from './base/heading'
import { Input } from './base/input'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Field } from './base/fieldset'
import { Listbox, ListboxLabel, ListboxOption } from './base/listbox'
import { Text } from './base/text'
import { useInfiniteScroll } from '@/helpers/useInfiniteScroll'
import { Inline as Loading } from '@/components/loading'
import { SortOrder } from '@/types'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'

export interface Row {
  id: string
  columns: (ReactNode | string | number | null | undefined)[]
  link?: string
}

export interface TableProps {
  pageSize?: number
  title?: string
  hiddenColumns?: string[]
  fixedHeight?: boolean
  onIndex?: boolean
}

interface CursorVariables<Filters, SortField> {
  before?: string | null
  after?: string | null
  first?: number
  last?: number
  filters?: Filters
  order?: SortField
}

interface Filter<Filters> {
  index: number
  type: 'text' | 'select'
  setValue: (
    setFilterValues: React.Dispatch<React.SetStateAction<Filters>>,
    value: string | undefined
  ) => void
  placeholder?: string
  options?: { value: string, display?: ReactNode | string }[]
}

interface ColumnHeading<SortFields> {
  name: string
  sortField?: keyof SortFields
  defaultDirection?: 'asc' | 'desc'
}

type SortField<SortFields> = Partial<Record<keyof SortFields, SortOrder>>

interface Props<Filters, SortFields> extends TableProps {
  headings: ColumnHeading<SortFields>[]
  rows: Row[]
  refetch: (variables: CursorVariables<Filters, SortField<SortFields>>) => void
  moreToFetch: boolean | undefined
  fetchMore: () => void
  onRowClick?: (id: string) => void
  filters?: Filter<Filters>[]
  initialFilters?: Filters
  actionButton?: ReactNode
  defaultSort?: SortField<SortFields>
}

export const Table = <Filters = { idEq: string }, SortFields = string[]>({
  title,
  headings,
  filters,
  initialFilters,
  rows,
  refetch,
  moreToFetch = false,
  fetchMore,
  onRowClick,
  actionButton,
  hiddenColumns = [],
  defaultSort = {},
  fixedHeight = false
}: Props<Filters, SortFields>) => {
  const [filterValues, setFilterValues] = useState<Filters>(initialFilters || {} as Filters)
  const [sortValues, setSortValues] = useState<SortField<SortFields>>(defaultSort)
  const searchTimeout = useRef<NodeJS.Timeout>(null)

  const { setLoaderElement } = useInfiniteScroll({ fetchMore })

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      refetch({ filters: filterValues, order: sortValues })
    }, 150)
  }, [filterValues, sortValues, searchTimeout, refetch])

  const sortBy = (column: string) => {
    const columnDetails = headings.find(h => h.name === column)
    if (!columnDetails || !columnDetails.sortField || !columnDetails.defaultDirection) return

    const sortField = columnDetails.sortField

    if (sortValues?.[sortField] == SortOrder.Asc) {
      setSortValues({ [sortField]: SortOrder.Desc } as SortField<SortFields>)
    } else if (sortValues?.[sortField] == SortOrder.Desc) {
      setSortValues({ [sortField]: SortOrder.Asc } as SortField<SortFields>)
    } else {
      setSortValues({ [sortField]: columnDetails.defaultDirection } as SortField<SortFields>)
    }
  }

  const filterAt = (index: number) => {
    return filters?.find(f => f.index == index)
  }

  const filterField = (filter?: Filter<Filters>) => {
    if (filter === undefined) return <></>

    switch (filter.type) {
      case 'text':
        return (
          <Field>
            <Input
              placeholder={filter.placeholder}
              onChange={e => filter.setValue(setFilterValues, e.currentTarget.value || undefined)}
            />
          </Field>
        )
      case 'select':
        return (
          <Field>
            <Listbox onChange={(value: string) => filter.setValue(setFilterValues, value)}>
              <ListboxOption key="empty" value={undefined}>
                <ListboxLabel>All</ListboxLabel>
              </ListboxOption>
              { filter.options?.map(option => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{ option.display || option.value }</ListboxLabel>
                </ListboxOption>
              )) }
            </Listbox>
          </Field>
        )
    }
  }

  const anyActiveFilters = (filters || []).filter(f => !hiddenColumns.includes(headings[f.index].name)).length > 0

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          { title && <Heading className="text-nowrap">{ title }</Heading> }
          { actionButton && <span className="w-full flex justify-end">{ actionButton }</span>}
        </div>
        <Base.Table className={`${fixedHeight ? 'h-96' : ''}`} striped>
          <Base.TableHead className="sticky top-0 bg-white dark:bg-zinc-900 z-10">
            <Base.TableRow>
              { headings.map(heading => {
                if (hiddenColumns.includes(heading.name)) return undefined
                return (
                  <Base.TableHeader key={heading.name}>
                    <span
                      className={`flex flex-row items-center gap-2 ${
                        heading.sortField
                          ? `hover:text-white ${sortValues[heading.sortField] ? 'text-white font-bold' : ''}`
                          : ''
                      }`}
                      onClick={() => sortBy(heading.name)}
                    >
                      { heading.name }
                      {
                        heading.sortField
                          ? sortValues[heading.sortField]
                            ? sortValues[heading.sortField] == SortOrder.Asc
                              ? <ChevronDownIcon className="size-4" />
                              : <ChevronUpIcon className="size-4" />
                            : heading.defaultDirection == SortOrder.Asc
                              ? <ChevronDownIcon className="size-4" />
                              : <ChevronUpIcon className="size-4" />
                          : <></>
                      }
                    </span>
                  </Base.TableHeader>
                )
              })}
            </Base.TableRow>
            { /* this one is the top sticky border */ }
            <Base.TableRow>
              <th className="h-[1px] p-0 bg-zinc-950/10 dark:bg-white/10" colSpan={headings.length} />
            </Base.TableRow>
            { /* this one is to get the stripey table back in order */ }
            <Base.TableRow />
            {
              anyActiveFilters &&
                <Base.TableRow>
                  { headings.map((heading, index) => {
                    if (hiddenColumns.includes(heading.name)) return undefined
                    return <Base.TableHeader key={index}>{ filterField(filterAt(index)) }</Base.TableHeader>
                  })}
                </Base.TableRow>
            }
            { /* this one is the bottom sticky border */ }
            <Base.TableRow>
              <th className="h-[1px] p-0 bg-zinc-950/10 dark:bg-white/10" colSpan={headings.length} />
            </Base.TableRow>
          </Base.TableHead>
          <Base.TableBody className={`${fixedHeight ? 'max-h-72 overflow-y-scroll' : ''}`}>
            { rows.length == 0 && <Base.TableRow>
              <Base.TableCell colSpan={headings.length}>
                <div className="flex p-4 items-center justify-center">
                  <Text>Nothing to see here... yet</Text>
                </div>
              </Base.TableCell>
            </Base.TableRow> }
            { rows.map(row => (
              <Base.TableRow key={row.id} href={row.link} onClick={() => onRowClick ? onRowClick(row.id) : {}}>
                { row.columns.map((column, index) => {
                  if (hiddenColumns.includes(headings[index].name)) return undefined
                  return <Base.TableCell key={index}>{ column }</Base.TableCell>
                }) }
              </Base.TableRow>
            ))}
            {
              moreToFetch
                ? <Base.TableRow>
                  <Base.TableCell colSpan={headings.length}>
                    <div ref={setLoaderElement} className="flex p-4 items-center justify-center">
                      <Loading />
                    </div>
                  </Base.TableCell>
                </Base.TableRow>
                : rows.length == 0
                  ? <></>
                  : <Base.TableRow>
                    <Base.TableCell colSpan={headings.length}>
                      <div className="flex p-4 items-center justify-center">
                        <div className="h-full w-full flex flex-row justify-center items-center gap-6">
                          <Text>That's all we've got for now</Text>
                        </div>
                      </div>
                    </Base.TableCell>
                  </Base.TableRow>
            }
          </Base.TableBody>
        </Base.Table>
      </div>
    </>
  )
}
